/* eslint-disable react-native/no-inline-styles */
import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import i18n from 'i18n-js';

import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { getBoxShadowStyle } from '@styles/shadow';
import { SmallLoading } from '@components/loading';
import Svg from '@styles/icons/svg';
import Button from '@styles/buttons';

import TouchableElement from '@components/common/touchableElement';
import { isPhone } from '@helpers';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { ScaleFunction } from '@components/operatorPanel/interfaces';

const LinePopupButton = ({
  C,
  loading,
  borderBottom = false,
  shadow = true,
  borderRadiusTop = true,
  borderRadiusBottom = true,
  fontTitle: customFontTitle,
  fontText: customFontText,
  title,
  text,
  popupText,
  popupBtnText,
  popupAdditionalText,
  popupBtnHandlePress,
  status,
  orderNo,
  scale = initialScale,
  font = initialFont,
}: {
  C: any;
  loading?: boolean;
  borderBottom?: boolean;
  shadow?: boolean;
  borderRadiusTop?: boolean;
  borderRadiusBottom?: boolean;
  fontTitle?: string;
  fontText?: string;
  title?: string;
  text?: string;
  popupText?: string;
  popupBtnText?: string;
  popupAdditionalText?: string;
  popupBtnHandlePress?: () => any;
  status: 'running' | 'skipped' | 'completed' | 'notStartReady' | 'readyToStart';
  orderNo?: number | string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const animatedHeight = useRef(new Animated.Value(0)).current;

  const isButton = popupBtnText && popupBtnHandlePress;

  const statusIcon = () => {
    switch (status) {
      case 'running':
        return 'runningMan';
      case 'skipped':
        return 'fastForward';
      case 'completed':
        return 'checkboxGrey';
      case 'notStartReady':
        return 'hourglass';
      case 'readyToStart':
        return 'readyToRunMan';
    }
  };

  const statusText = () => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'skipped':
        return 'Skipped';
      case 'completed':
        return 'Completed';
      case 'notStartReady':
        return 'Not Start Ready';
      case 'readyToStart':
        return 'Ready to Start';
    }
  };

  const handleToggle = () => {
    setIsOpen((prevState) => {
      Animated.timing(animatedHeight, {
        toValue: !prevState ? 1 : 0,
        duration: 400,
        useNativeDriver: false,
      }).start();

      return !prevState;
    });
  };

  return (
    <View
      style={[
        { userSelect: 'none' },
        shadow && {
          borderRadius: scale(SCREEN_BORDER_RADIUS),
        },
        shadow && getBoxShadowStyle({ type: 'Field', C }),
      ]}
    >
      
      <TouchableElement onPress={handleToggle} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}>
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              {
                backgroundColor: C(pressHoverColor(modalFieldBg) || 'Background/Light/Field'),
                width: '100%',
                justifyContent: 'center',
                alignContent: 'center',
              },
              borderRadiusTop && {
                borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
              },
              borderRadiusBottom && {
                borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
              },
              borderBottom && {
                borderBottomColor: C('Hairline/Light/No trans'),
                borderBottomWidth: StyleSheet.hairlineWidth,
              },
              boxStyle,
            ]}
          >
            {loading ? (
              <SmallLoading />
            ) : (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: scale(15), gap: scale(15) }}>
                  <View style={{ alignSelf: 'flex-start', paddingTop: scale(8) }}>
                    <Text numberOfLines={1} style={[font.Headline, { color: C(isOpen ? 'Label/Light/Primary' : 'Label/Light/Secondary') }]}>
                      {orderNo}
                    </Text>
                  </View>
                  <View style={[{ flexDirection: 'row', alignItems: 'center', flex: 1 }]}>
                    <View style={{ paddingVertical: scale(8), flex: 1, marginRight: scale(5) }}>
                      <Text
                        numberOfLines={1}
                        style={[
                          font[customFontTitle || 'Headline'],
                          { color: C(isOpen ? 'Label/Light/Primary' : 'Label/Light/Secondary'), height: scale(22) },
                        ]}
                      >
                        {title}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          font[customFontText || 'Subhead 2'],
                          { color: C(isOpen ? 'Label/Light/Primary' : 'Label/Light/Secondary'), height: scale(22) },
                        ]}
                      >
                        {text}
                      </Text>
                    </View>
                    <View style={{ marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: scale(5) }}>
                      <Text
                        numberOfLines={1}
                        style={[font[customFontText || 'Footnote'], { color: C('Label/Light/Secondary'), textAlign: 'center' }]}
                      >
                        {i18n.t(statusText())}
                      </Text>
                      <Svg icon={statusIcon()} width={20} height={20} />
                      <View style={{ transform: [{ rotate: isOpen ? '-90deg' : '90deg' }] }}>
                        <Svg icon="chevronRightLight" height={scale(11)} width={scale(7)} color={C('Label/Light/Secondary')} />
                      </View>
                    </View>
                  </View>
                </View>
                <Animated.View
                  style={{
                    overflow: 'hidden',
                    maxHeight: animatedHeight.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0, scale(200)],
                    }),
                  }}
                >
                  <View style={{ paddingLeft: scale(41), paddingRight: scale(15), marginTop: scale(2) }}>
                    <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), marginBottom: scale(12) }]}>
                      {popupText}
                    </Text>
                    {isButton && (
                      <View style={{ width: isPhone ? '100%' : '70%', marginHorizontal: 'auto', marginBottom: scale(7) }}>
                        <Button {...{ variant: 'Dialog M', type: 'Primary', onPress: popupBtnHandlePress, borderRadius: 12, scale, font }}>
                          {i18n.t(popupBtnText)}
                        </Button>
                      </View>
                    )}
                    {popupAdditionalText && (
                      <Text
                        numberOfLines={1}
                        style={[font['Caption 1'], { color: C('Label/Light/Secondary'), marginHorizontal: 'auto', marginBottom: scale(9) }]}
                      >
                        {popupAdditionalText}
                      </Text>
                    )}
                  </View>
                </Animated.View>
              </>
            )}
          </View>
        )}
      </TouchableElement>
    </View>
  );
};

export default LinePopupButton;
